import { QueryField, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import RadioEditor from './RadioEditor';
import CheckboxEditor from './CheckboxEditor';
import SubscriptionToggles from './SubscriptionToggles';
import SelectEditor from './SelectEditor';
import BetweenEditor from './BetweenEditor';
import InputEditor from './InputEditor';

interface ValueEditorProps {
    ruleId: string;
    field?: QueryField;
    operator: string;
    value: QueryRule['value'];
    onChange: (value: QueryRule['value']) => void;
    compact?: boolean;
    betweenSwapIfInverted?: boolean;
    fillHeight?: boolean;
}

const SUBSCRIPTION_FIELD_NAME = 'CUSTOMER_SUBSCRIPTIONS_TYPE';

const toStringValue = (value: QueryRule['value']): string => {
    if (value === null || value === undefined) return '';
    return Array.isArray(value) ? value.join(',') : String(value);
};

const toSelectValue = (value: QueryRule['value']): string | string[] => (Array.isArray(value) ? value : toStringValue(value));

function ValueEditor({ ruleId, field, operator, value, onChange, compact = false, betweenSwapIfInverted = false, fillHeight = false }: ValueEditorProps) {
    const { t } = useLanguage();

    if (!field) {
        return (
            <InputEditor
                type="text"
                value={toStringValue(value)}
                onChange={onChange}
                placeholder={t('pages.clients.queryBuilder.simple.selectFieldFirstPlaceholder')}
            />
        );
    }

    const { valueEditorType, inputType } = field;
    const values = field.values ?? [];

    if (operator === 'between') {
        return <BetweenEditor value={toStringValue(value)} inputType={inputType} onChange={onChange} swapIfInverted={betweenSwapIfInverted} />;
    }

    if (valueEditorType === 'checkbox') {
        if (field.name === SUBSCRIPTION_FIELD_NAME && values.length > 0) {
            return <SubscriptionToggles value={value} options={values} onChange={onChange} />;
        }
        return <CheckboxEditor ruleId={ruleId} value={value} values={values} onChange={onChange} compact={compact} fillHeight={fillHeight} />;
    }

    if (valueEditorType === 'select' || valueEditorType === 'multiselect') {
        return (
            <SelectEditor
                ruleId={ruleId}
                value={toSelectValue(value)}
                values={values}
                isMultiSelect={compact || valueEditorType === 'multiselect' || operator === 'in' || operator === 'not in'}
                compact={compact}
                onChange={onChange}
            />
        );
    }

    if (valueEditorType === 'radio' && values.length > 0) {
        return <RadioEditor value={toStringValue(value)} values={values} onChange={onChange} />;
    }

    if (inputType === 'number') {
        return <InputEditor type="number" value={toStringValue(value)} onChange={onChange} placeholder={t('pages.clients.queryBuilder.simple.numberPlaceholder')} />;
    }

    if (inputType === 'date') {
        return <InputEditor type="date" value={toStringValue(value)} onChange={onChange} />;
    }

    return <InputEditor type="text" value={toStringValue(value)} onChange={onChange} placeholder={t('pages.clients.queryBuilder.simple.textPlaceholder')} />;
}

export default ValueEditor;
